import type { NodeHandler, PositionedNodeHandler } from './handlers/types';
import type { MarkHandler } from './marks/types';
import type { GroupHandler } from './handlers/group-handler';
import {
  AttachmentHandler,
  DocHandler,
  ParagraphHandler,
  HeadingHandler,
  BulletListHandler,
  OrderedListHandler,
  ListItemHandler,
  CodeBlockHandler,
  BlockquoteHandler,
  HardBreakHandler,
  HorizontalRuleHandler,
  DisclaimerHandler,
  EmojiHandler,
  ExpressionHandler,
  MentionHandler,
  ResponseShapeHandler,
  InputShapeHandler,
  TableHandler,
  TableRowHandler,
  TableCellHandler,
  TableHeaderHandler,
} from './handlers';
import {
  BoldMark,
  ItalicMark,
  CodeMark,
  StrikeMark,
  LinkMark,
  UnderlineMark,
} from './marks';

export class HandlerContainer {
  // Registry of node handlers
  private nodeHandlers: Map<string, NodeHandler | PositionedNodeHandler> =
    new Map();

  // Registry of mark handlers
  private markHandlers: Map<string, MarkHandler> = new Map();

  // Registry of group handlers (multiple handlers can handle same node type)
  private groupHandlers: Map<string, GroupHandler[]> = new Map();

  constructor(
    nodeHandlers?: (NodeHandler | PositionedNodeHandler)[],
    markHandlers?: MarkHandler[],
    groupHandlers?: GroupHandler[]
  ) {
    // Register default node handlers
    const defaultNodeHandlers: (NodeHandler | PositionedNodeHandler)[] =
      nodeHandlers ?? [
        new DocHandler(),
        new ParagraphHandler(),
        new HeadingHandler(),
        new BulletListHandler(),
        new OrderedListHandler(),
        new ListItemHandler(),
        new CodeBlockHandler(),
        new BlockquoteHandler(),
        new HardBreakHandler(),
        new HorizontalRuleHandler(),
        new DisclaimerHandler(),
        new EmojiHandler(),
        new ExpressionHandler(),
        new MentionHandler(),
        new ResponseShapeHandler(),
        new InputShapeHandler(),
        new AttachmentHandler(),
        new TableHandler(),
        new TableRowHandler(),
        new TableCellHandler(),
        new TableHeaderHandler(),
      ];

    for (const handler of defaultNodeHandlers) {
      this.nodeHandlers.set(handler.type, handler);
    }

    // Register default mark handlers
    const defaultMarkHandlers: MarkHandler[] = markHandlers ?? [
      new BoldMark(),
      new ItalicMark(),
      new CodeMark(),
      new StrikeMark(),
      new LinkMark(),
      new UnderlineMark(),
    ];

    for (const handler of defaultMarkHandlers) {
      this.markHandlers.set(handler.type, handler);
    }

    // Register group handlers
    if (groupHandlers) {
      for (const handler of groupHandlers) {
        if (!this.groupHandlers.has(handler.type)) {
          this.groupHandlers.set(handler.type, []);
        }
        this.groupHandlers.get(handler.type)!.push(handler);
      }
    }
  }

  getNodeHandler(
    type: string
  ): NodeHandler | PositionedNodeHandler | undefined {
    return this.nodeHandlers.get(type);
  }

  getMarkHandler(type: string): MarkHandler | undefined {
    return this.markHandlers.get(type);
  }

  getGroupHandlers(type: string): GroupHandler[] | undefined {
    return this.groupHandlers.get(type);
  }

  getAllGroupHandlers(): Map<string, GroupHandler[]> {
    return this.groupHandlers;
  }

  getAllNodeHandlers(): (NodeHandler | PositionedNodeHandler)[] {
    return Array.from(this.nodeHandlers.values());
  }

  getAllMarkHandlers(): MarkHandler[] {
    return Array.from(this.markHandlers.values());
  }

  getAllGroupHandlersFlat(): GroupHandler[] {
    return Array.from(this.groupHandlers.values()).flat();
  }
}
